import { observer } from 'mobx-react-lite'
import { Avatar, Box, NameValueRecord, Tooltip, Typography } from 'shared/ui'
import { EmptyCell, ExpandCell, type IColumn } from 'shared/ui/TableNew'
import { DayjsFormats, numberFormat } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { LINK_CONVERSION_REVENUE_TRACKING } from 'shared/constants/links'
import { BroadcastStatus } from 'entities/Broadcast/ui/BroadcastStatus'
import { BroadcastRecurringLabel, BroadcastType, type IRowBroadcast } from 'entities/Broadcast'
import { type IBroadcastType } from 'entities/Broadcast/api/types'
import { RowBroadcastOneTime } from 'entities/Broadcast/model/Table/RowBroadcastOneTime'
import { RowBroadcastRecurring } from 'entities/Broadcast/model/Table/RowBroadcastRecurring'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import styles from './styles.module.scss'

const ringlessColumnsIds = [
  'name',
  'type',
  'status',
  'sent_at',
  'total_count',
  'success_count',
  'reply_count',
  'no_replies_count',
  'undelivered',
  'send_from',
  'created_by',
]

const NameCell = observer(<TRow extends IRowBroadcast = IRowBroadcast>({ row }: { row: TRow }) => (
  <div className={styles.nameCellWrapper}>
    <Typography
      variant={'body-rg-regular'}
      ellipsis
      tooltipProps={{
        label: row.name,
      }}
      ariaLabel={'name'}
    >
      {row.name}
    </Typography>
    {row instanceof RowBroadcastRecurring && <BroadcastRecurringLabel broadcast={row.origin} />}
  </div>
))

const StatisticCell = ({
  isEmpty,
  isHover,
  value,
  percent,
}: {
  isEmpty?: boolean | undefined
  isHover: boolean | undefined
  value: number | undefined | null
  percent: number | undefined
}) => {
  if (isEmpty) {
    return <EmptyCell />
  }
  return <>{isHover ? `${percent}%` : value && numberFormat({ value })}</>
}

const getColumnsByType = <TRow,>(type: IBroadcastType, columns: IColumn<TRow>[]) => {
  if (type === 'rvm') {
    return columns.filter((column) => ringlessColumnsIds.includes(column.field))
  }

  if (type === 'sms') {
    return columns.filter((column) => column.field !== 'expandedColumn')
  }

  if (type === 'recurring') {
    return columns.map((column) => ({ ...column, isSortable: column.field === 'name' }))
  }

  return columns
}

export const getColumnsOneTime = <TRow extends RowBroadcastOneTime>(
  type: IBroadcastType,
  entities?: IEntitiesStore
): IColumn<TRow>[] => {
  const quietHoursColumn: IColumn<TRow> | null =
    entities?.featureFlagsStore.quiet_hours &&
    entities.organizationStore.quietHoursSettings?.is_active
      ? {
          isSortable: true,
          sortKey: 'quiet_hours',
          field: 'quiet_hours_count',
          name: 'Quiet hours',
          renderRowCell: (row: TRow, _, { isHover }) => (
            <StatisticCell
              isHover={isHover}
              value={row.origin.statistics?.broadcastBaseStatistic.quiet_hours_count}
              percent={row.origin.statistics?.broadcastBaseStatistic.quiet_hours_percent}
            />
          ),
        }
      : null

  const allColumns: IColumn<TRow>[] = [
    {
      field: 'expandedColumn',
      width: '40px',
      minWidth: '40px',
      renderRowCell: (row) => <ExpandCell row={row} />,
      renderHeaderCell: () => null,
      getIsDisableLink: () => false,
      isHideForManage: true,
      padding: 0,
    },
    {
      isSortable: true,
      field: 'name',
      name: 'Name',
      width: '100%',
      minWidth: '362px',
      maxWidth: '362px',
      isDisabledChangeVisible: true,
      paddingInExpand: '32px',
      renderRowCell: (row) => <NameCell row={row} />,
    },
    {
      isSortable: true,
      field: 'type',
      name: 'Type',
      width: '72px',
      isDisabledChangeVisible: true,
      renderRowCell: (row) => <BroadcastType type={row.type} />,
    },
    {
      isSortable: true,
      field: 'status',
      name: 'Status',
      isDisabledChangeVisible: true,
      renderRowCell: (row) =>
        row.status && (
          <Tooltip label={row.isInvalid && 'Suspicious content detected'} placement={'top'}>
            <BroadcastStatus status={row.status} />
          </Tooltip>
        ),
    },
    {
      isSortable: true,
      sortKey: 'send_time',
      field: 'sent_at',
      name: 'Start time',
      renderRowCell: (row) =>
        (row.origin.send_at || row.origin.sent_at) &&
        uiStore
          .dayjs(row.origin.send_at || row.origin.sent_at)
          .format(DayjsFormats.fullWithAtDash2),
    },
    {
      isSortable: true,
      sortKey: 'total',
      field: 'total_count',
      name: 'Total',
      renderRowCell: (row) =>
        row.origin.statistics?.broadcastBaseStatistic.total_count &&
        numberFormat({ value: row.origin.statistics?.broadcastBaseStatistic.total_count }),
    },
    ...(quietHoursColumn ? [quietHoursColumn] : []),
    {
      isSortable: true,
      sortKey: 'delivered',
      field: 'success_count',
      name: 'Delivered',
      renderRowCell: (row, _, { isHover }) => (
        <StatisticCell
          isHover={isHover}
          value={row.origin.statistics?.broadcastBaseStatistic.success_count}
          percent={row.origin.statistics?.broadcastBaseStatistic.success_percent}
        />
      ),
    },
    {
      isSortable: true,
      sortKey: 'replies',
      field: 'reply_count',
      name: 'Replies',
      renderRowCell: (row, _, { isHover }) => (
        <StatisticCell
          isEmpty={row.origin.statistics?.broadcastBaseStatistic.reply_count === null}
          isHover={isHover}
          value={row.origin.statistics?.broadcastBaseStatistic.reply_count}
          percent={row.origin.statistics?.broadcastBaseStatistic.reply_percent}
        />
      ),
    },
    {
      isSortable: true,
      sortKey: 'no_replies',
      field: 'no_replies_count',
      name: 'No replies',
      renderRowCell: (row, _, { isHover }) => (
        <StatisticCell
          isEmpty={row.origin.statistics?.broadcastBaseStatistic.no_replies_count === null}
          isHover={isHover}
          value={row.origin.statistics?.broadcastBaseStatistic.no_replies_count}
          percent={row.origin.statistics?.broadcastBaseStatistic.no_replies_percent}
        />
      ),
    },
    {
      isSortable: true,
      field: 'clicks',
      name: 'Clicks',
      renderRowCell: (row, _, { isHover }) => {
        return (
          <StatisticCell
            isEmpty={
              row.type === 'ringless' ||
              row.origin.statistics?.broadcastBaseStatistic.clicks_count === null
            }
            isHover={isHover}
            value={row.origin.statistics?.broadcastBaseStatistic.clicks_count}
            percent={row.origin.statistics?.broadcastBaseStatistic.clicks_percent}
          />
        )
      },
    },
    {
      isSortable: true,
      field: 'conversions',
      name: 'Conversions',
      renderRowCell: (row, _, { isHover }) => (
        <StatisticCell
          isEmpty={
            row.type === 'ringless' ||
            row.origin.statistics?.broadcastBaseStatistic.conversions_count === undefined
          }
          isHover={isHover}
          value={row.origin.statistics?.broadcastBaseStatistic.conversions_count}
          percent={row.origin.statistics?.broadcastBaseStatistic.conversions_percent}
        />
      ),
    },
    {
      isSortable: true,
      sortKey: 'revenue',
      field: 'revenue_cost',
      name: 'Revenue',
      tooltipQuestionProps: {
        width: 221,
        placement: 'top',
        label: [
          <Typography
            key={'revenue_tooltip'}
            variant={'body-md-regular'}
            ariaLabel={'BroadcastRevenueTooltip'}
            className={styles.tooltipText}
          >
            Use Conversion Tracking to track the revenue your Broadcasts generate.{' '}
            <Typography key={'link'} variant={'body-md-medium'} linkLight ariaLabel={'LearnMore'}>
              <a href={LINK_CONVERSION_REVENUE_TRACKING} target={'_blank'} rel='noreferrer'>
                Learn more
              </a>
            </Typography>
          </Typography>,
        ],
      },
      renderRowCell: (row) => row.origin.statistics?.broadcastBaseStatistic.revenue_cost,
    },
    {
      isSortable: true,
      field: 'undelivered',
      name: 'Undelivered',
      isHideByDefault: type !== 'rvm',
      renderRowCell: (row, _, { isHover }) => (
        <StatisticCell
          isHover={isHover}
          value={row.origin.statistics?.broadcastBaseStatistic.failed_count}
          percent={row.origin.statistics?.broadcastBaseStatistic.failed_percent}
        />
      ),
    },
    {
      isSortable: true,
      field: 'unsubscribed',
      name: 'Unsubscribed',
      isHideByDefault: true,
      renderRowCell: (row, _, { isHover }) => (
        <StatisticCell
          isHover={isHover}
          isEmpty={row.type === 'ringless'}
          value={row.origin.statistics?.broadcastBaseStatistic.unsubscribed_count || 0}
          percent={row.origin.statistics?.broadcastBaseStatistic.unsubscribed_percent}
        />
      ),
    },
    {
      isSortable: true,
      field: 'send_from',
      name: 'Send from',
      isHideByDefault: true,
      renderRowCell: (row) => {
        if (!row.origin.sendFromNumbersProps) return null

        return <NameValueRecord {...row.origin.sendFromNumbersProps} />
      },
    },
    {
      field: 'created_by',
      name: 'Created by',
      isHideByDefault: true,
      renderRowCell: (row) => {
        if (!row.user?.avatarInfo && !row.user?.displayInfo) return <EmptyCell />
        return (
          <Box display={'flex'} gap={'6px'}>
            {row.user?.avatarInfo && <Avatar info={row.user?.avatarInfo} size={16} />}
            <Typography
              variant={'body-md-regular'}
              ellipsis
              tooltipProps={{ label: row.user?.displayInfo }}
              ariaLabel={'displayInfo'}
            >
              {row.user?.displayInfo}
            </Typography>
          </Box>
        )
      },
    },
  ]

  return getColumnsByType(type, allColumns)
}

export const getColumnsCombo = <TRow extends IRowBroadcast>(
  type: IBroadcastType,
  entities?: IEntitiesStore
): IColumn<TRow>[] => {
  const quietHoursColumn: IColumn<TRow> | null =
    entities?.featureFlagsStore.quiet_hours &&
    entities.organizationStore.quietHoursSettings?.is_active
      ? {
          isSortable: true,
          sortKey: 'quiet_hours',
          field: 'quiet_hours_count',
          name: 'Quiet hours',
          renderRowCell: (row: TRow, _, { isHover }) =>
            row instanceof RowBroadcastOneTime ? (
              <StatisticCell
                isHover={isHover}
                value={row.origin.statistics?.broadcastBaseStatistic.quiet_hours_count}
                percent={row.origin.statistics?.broadcastBaseStatistic.quiet_hours_percent}
              />
            ) : (
              <EmptyCell />
            ),
        }
      : null

  const allColumns: IColumn<TRow>[] = [
    {
      field: 'expandedColumn',
      width: '40px',
      minWidth: '40px',
      renderRowCell: (row) => <ExpandCell row={row} />,
      renderHeaderCell: () => null,
      getIsDisableLink: (row) => row instanceof RowBroadcastRecurring,
      isHideForManage: true,
      padding: 0,
    },
    {
      isSortable: true,
      field: 'name',
      name: 'Name',
      width: '100%',
      minWidth: '362px',
      maxWidth: '362px',
      isDisabledChangeVisible: true,
      paddingInExpand: '32px',
      renderRowCell: (row) => <NameCell row={row} />,
    },
    {
      isSortable: true,
      field: 'type',
      name: 'Type',
      width: '72px',
      isDisabledChangeVisible: true,
      renderRowCell: (row) => <BroadcastType type={row.type} />,
    },
    {
      isSortable: true,
      field: 'status',
      name: 'Status',
      isDisabledChangeVisible: true,
      renderRowCell: (row) =>
        row.status && (
          <Tooltip label={row.isInvalid && 'Suspicious content detected'} placement={'top'}>
            <BroadcastStatus status={row.status} />
          </Tooltip>
        ),
    },
    {
      isSortable: true,
      sortKey: 'send_time',
      field: 'sent_at',
      name: 'Start time',
      renderRowCell: (row) =>
        row instanceof RowBroadcastOneTime ? (
          (row.origin.send_at || row.origin.sent_at) &&
          uiStore
            .dayjs(row.origin.send_at || row.origin.sent_at)
            .format(DayjsFormats.fullWithAtDash2)
        ) : (
          <EmptyCell />
        ),
    },
    {
      isSortable: true,
      sortKey: 'total',
      field: 'total_count',
      name: 'Total',
      renderRowCell: (row) =>
        row instanceof RowBroadcastOneTime ? (
          row.origin.statistics?.broadcastBaseStatistic.total_count &&
          numberFormat({ value: row.origin.statistics?.broadcastBaseStatistic.total_count })
        ) : (
          <EmptyCell />
        ),
    },
    ...(quietHoursColumn ? [quietHoursColumn] : []),
    {
      isSortable: true,
      sortKey: 'delivered',
      field: 'success_count',
      name: 'Delivered',
      renderRowCell: (row, _, { isHover }) =>
        row instanceof RowBroadcastOneTime ? (
          <StatisticCell
            isHover={isHover}
            value={row.origin.statistics?.broadcastBaseStatistic.success_count}
            percent={row.origin.statistics?.broadcastBaseStatistic.success_percent}
          />
        ) : (
          <EmptyCell />
        ),
    },
    {
      isSortable: true,
      sortKey: 'replies',
      field: 'reply_count',
      name: 'Replies',
      renderRowCell: (row, _, { isHover }) =>
        row instanceof RowBroadcastOneTime ? (
          <StatisticCell
            isEmpty={row.origin.statistics?.broadcastBaseStatistic.reply_count === null}
            isHover={isHover}
            value={row.origin.statistics?.broadcastBaseStatistic.reply_count}
            percent={row.origin.statistics?.broadcastBaseStatistic.reply_percent}
          />
        ) : (
          <EmptyCell />
        ),
    },
    {
      isSortable: true,
      sortKey: 'no_replies',
      field: 'no_replies_count',
      name: 'No replies',
      renderRowCell: (row, _, { isHover }) =>
        row instanceof RowBroadcastOneTime ? (
          <StatisticCell
            isEmpty={row.origin.statistics?.broadcastBaseStatistic.no_replies_count === null}
            isHover={isHover}
            value={row.origin.statistics?.broadcastBaseStatistic.no_replies_count}
            percent={row.origin.statistics?.broadcastBaseStatistic.no_replies_percent}
          />
        ) : (
          <EmptyCell />
        ),
    },
    {
      isSortable: true,
      field: 'clicks',
      name: 'Clicks',
      renderRowCell: (row, _, { isHover }) => {
        return row instanceof RowBroadcastOneTime ? (
          <StatisticCell
            isEmpty={
              row.type === 'ringless' ||
              row.origin.statistics?.broadcastBaseStatistic.clicks_count === null
            }
            isHover={isHover}
            value={row.origin.statistics?.broadcastBaseStatistic.clicks_count}
            percent={row.origin.statistics?.broadcastBaseStatistic.clicks_percent}
          />
        ) : (
          <EmptyCell />
        )
      },
    },
    {
      isSortable: true,
      field: 'conversions',
      name: 'Conversions',
      renderRowCell: (row, _, { isHover }) =>
        row instanceof RowBroadcastOneTime ? (
          <StatisticCell
            isEmpty={
              row.type === 'ringless' ||
              row.origin.statistics?.broadcastBaseStatistic.conversions_count === undefined
            }
            isHover={isHover}
            value={row.origin.statistics?.broadcastBaseStatistic.conversions_count}
            percent={row.origin.statistics?.broadcastBaseStatistic.conversions_percent}
          />
        ) : (
          <EmptyCell />
        ),
    },
    {
      isSortable: true,
      sortKey: 'revenue',
      field: 'revenue_cost',
      name: 'Revenue',
      tooltipQuestionProps: {
        width: 221,
        placement: 'top',
        label: [
          <Typography
            key={'revenue_tooltip'}
            variant={'body-md-regular'}
            ariaLabel={'BroadcastRevenueTooltip'}
            className={styles.tooltipText}
          >
            Use Conversion Tracking to track the revenue your Broadcasts generate.{' '}
            <Typography key={'link'} variant={'body-md-medium'} linkLight ariaLabel={'LearnMore'}>
              <a href={LINK_CONVERSION_REVENUE_TRACKING} target={'_blank'} rel='noreferrer'>
                Learn more
              </a>
            </Typography>
          </Typography>,
        ],
      },
      renderRowCell: (row) =>
        row instanceof RowBroadcastOneTime
          ? row.origin.statistics?.broadcastBaseStatistic.revenue_cost
          : undefined,
    },
    {
      isSortable: true,
      field: 'undelivered',
      name: 'Undelivered',
      isHideByDefault: type !== 'rvm',
      renderRowCell: (row, _, { isHover }) =>
        row instanceof RowBroadcastOneTime ? (
          <StatisticCell
            isHover={isHover}
            value={row.origin.statistics?.broadcastBaseStatistic.failed_count}
            percent={row.origin.statistics?.broadcastBaseStatistic.failed_percent}
          />
        ) : (
          <EmptyCell />
        ),
    },
    {
      isSortable: true,
      field: 'unsubscribed',
      name: 'Unsubscribed',
      isHideByDefault: true,
      renderRowCell: (row, _, { isHover }) =>
        row instanceof RowBroadcastOneTime ? (
          <StatisticCell
            isHover={isHover}
            isEmpty={row.type === 'ringless'}
            value={row.origin.statistics?.broadcastBaseStatistic.unsubscribed_count || 0}
            percent={row.origin.statistics?.broadcastBaseStatistic.unsubscribed_percent}
          />
        ) : (
          <EmptyCell />
        ),
    },
    {
      isSortable: true,
      field: 'send_from',
      name: 'Send from',
      isHideByDefault: true,
      renderRowCell: (row) =>
        row instanceof RowBroadcastOneTime ? (
          !!row.origin.sendFromNumbersProps && (
            <NameValueRecord {...row.origin.sendFromNumbersProps} />
          )
        ) : (
          <EmptyCell />
        ),
    },
    {
      field: 'created_by',
      name: 'Created by',
      isHideByDefault: true,
      renderRowCell: (row) => {
        if (!row.user?.avatarInfo && !row.user?.displayInfo) return <EmptyCell />
        return (
          <Box display={'flex'} gap={'6px'}>
            {row.user?.avatarInfo && <Avatar info={row.user?.avatarInfo} size={16} />}
            <Typography
              variant={'body-md-regular'}
              ellipsis
              tooltipProps={{ label: row.user?.displayInfo }}
              ariaLabel={'displayInfo'}
            >
              {row.user?.displayInfo}
            </Typography>
          </Box>
        )
      },
    },
  ]

  return getColumnsByType(type, allColumns)
}
